// Recurring Invoice — the right-docked drawers for one generated invoice:
// view, receive payment & make invoice, and edit (pending invoices only).
import React, { useEffect, useState } from "react";
import { Btn, Drawer, Fact, IC, SectHead } from "./RecurringParts";
import { PAY_MODES, daysTo, fmtD, fmtDLong, iso, lineOf, money2, num, today, totalsOf } from "./recurringModel";

export const InvoiceViewDrawer = ({ x, open, onClose, onReceive, onPrint, permit }) => (
  <Drawer
    open={open}
    onClose={onClose}
    title={x ? x.no : ""}
    sub={x ? x.custName + " · " + fmtDLong(x.date) : ""}
    footer={
      <>
        {x && <Btn icon={IC.print} onClick={() => onPrint(x)}>Print</Btn>}
        <span className="sp" />
        <Btn onClick={onClose}>Close</Btn>
        {x && x.pay === "Pending" && permit.add && (
          <Btn kind="grn" onClick={() => onReceive(x)}>
            Receive payment &amp; make invoice
          </Btn>
        )}
      </>
    }
  >
    {x && (
      <>
        <div className="axrc-amtbox">
          <div>
            <div className="l">Invoice total</div>
            <div className="s">
              {x.terms ? x.terms + " · " : ""}due {fmtD(x.due)}
            </div>
          </div>
          <div className="v">{money2(x.net)}</div>
        </div>
        <div className="axrc-facts" style={{ marginBottom: 14 }}>
          <Fact l="Raised from" v={x.profileNo || "—"} s={x.service} />
          <Fact l="Salesperson" v={x.salesperson || "—"} s={x.type} />
          <Fact l="Taxable" v={money2(x.taxable)} s={"VAT " + money2(x.vat)} />
          <Fact
            l="Payment"
            v={x.pay}
            cls={x.pay === "Paid" ? "good" : "warn"}
            s={
              x.pay === "Paid"
                ? [x.mode, x.paidOn ? "on " + fmtD(x.paidOn) : "", x.salesInvoiceNo ? "· " + x.salesInvoiceNo : ""].filter(Boolean).join(" ")
                : daysTo(x.due) < 0
                ? Math.abs(daysTo(x.due)) + " days late"
                : "due in " + daysTo(x.due) + " days"
            }
          />
        </div>
        <SectHead>Lines</SectHead>
        {x.items.length ? (
          <div className="axrc-vdoc">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Qty</th>
                  <th className="num">Rate</th>
                  <th className="num">Net</th>
                </tr>
              </thead>
              <tbody>
                {x.items.map((l) => (
                  <tr key={l.key}>
                    <td>
                      {l.productName}
                      {l.note ? <div className="axrc-dim small">{l.note}</div> : null}
                    </td>
                    <td className="num">{l.qty}</td>
                    <td className="num">{money2(l.rate)}</td>
                    <td className="num">{money2(lineOf(l).net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="axrc-nores">The server did not send the lines for this invoice.</div>
        )}
      </>
    )}
  </Drawer>
);

export const ReceiveDrawer = ({ x, open, onClose, onConfirm, ledgers, nextDue, busy }) => {
  const [f, setF] = useState({ date: iso(today()), amount: "", mode: PAY_MODES[0], ref: "", ledger: "" });
  const [err, setErr] = useState("");
  useEffect(() => {
    if (open && x) {
      setF({ date: iso(today()), amount: String(x.net), mode: PAY_MODES[0], ref: "", ledger: ledgers[0] ? ledgers[0].value : "" });
      setErr("");
    }
  }, [open, x, ledgers]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const go = () => {
    const amt = num(f.amount);
    if (!f.date) return setErr("Receipt date is required");
    if (!(amt > 0)) return setErr("Enter the amount received");
    if (amt > num(x.net) + 0.005) return setErr("The amount is more than the invoice total");
    if (!f.ledger) return setErr("Choose where the money is deposited");
    setErr("");
    onConfirm(x, { ...f, amount: amt });
  };
  return (
    <Drawer
      open={open}
      onClose={onClose}
      green
      title="Receive payment"
      sub={x ? x.no + " · " + x.custName : ""}
      footer={
        <>
          <span className="msg">{err}</span>
          <span className="sp" />
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn kind="grn" onClick={go} disabled={busy}>
            {busy ? "Posting…" : "Receive & make invoice"}
          </Btn>
        </>
      }
    >
      {x && (
        <>
          <div className="axrc-amtbox">
            <div>
              <div className="l">Amount due</div>
              <div className="s">{x.terms}</div>
            </div>
            <div className="v">{money2(x.net)}</div>
          </div>
          <div className="axrc-formgrid">
            <div className="axrc-f">
              <label htmlFor="rcRcDate">Receipt date</label>
              <input type="date" id="rcRcDate" value={f.date} onChange={set("date")} />
            </div>
            <div className="axrc-f">
              <label htmlFor="rcRcAmt">Amount received</label>
              <input className="mono" id="rcRcAmt" inputMode="decimal" value={f.amount} onChange={set("amount")} />
            </div>
            <div className="axrc-f sel">
              <label htmlFor="rcRcMode">Mode</label>
              <select id="rcRcMode" value={f.mode} onChange={set("mode")}>
                {PAY_MODES.map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
              {IC.caret}
            </div>
            <div className="axrc-f">
              <label htmlFor="rcRcRef">Reference</label>
              <input id="rcRcRef" placeholder="UTR / cheque no." value={f.ref} onChange={set("ref")} />
            </div>
            <div className="axrc-f sel full">
              <label htmlFor="rcRcAcct">Deposit to</label>
              <select id="rcRcAcct" value={f.ledger} onChange={set("ledger")}>
                {!ledgers.length && <option value="">No cash or bank ledger found</option>}
                {ledgers.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
              {IC.caret}
            </div>
          </div>
          <div className="axrc-callout">
            <b>What this does.</b> Posts the tax invoice against {x.profileNo || "the profile"}, records the receipt, clears the
            customer balance and marks this cycle paid.{nextDue ? " The next cycle still falls due on " + fmtD(nextDue) + "." : ""}
          </div>
        </>
      )}
    </Drawer>
  );
};

export const InvoiceEditDrawer = ({ x, open, onClose, onSave, busy }) => {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (open && x) setF({ date: x.date, due: x.due, items: x.items.map((l) => ({ ...l })) });
  }, [open, x]);
  const setLine = (i, ch) => setF((s) => ({ ...s, items: s.items.map((l, k) => (k === i ? { ...l, ...ch } : l)) }));
  const t = f ? totalsOf(f.items) : { taxable: 0, vat: 0, net: 0 };
  return (
    <Drawer
      open={open}
      onClose={onClose}
      icon={IC.edit}
      title={x ? "Edit " + x.no : ""}
      sub={x ? x.custName + " · pending, not yet posted" : ""}
      footer={
        <>
          <span className="axrc-dim">Net {money2(t.net)}</span>
          <span className="sp" />
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn kind="primary" onClick={() => onSave(x, f, t)} disabled={busy || !f || (f.due && f.date && f.due < f.date)}>
            Save changes
          </Btn>
        </>
      }
    >
      {f && (
        <>
          <div className="axrc-formgrid">
            <div className="axrc-f">
              <label htmlFor="rcEdDate">Invoice date</label>
              <input type="date" id="rcEdDate" value={f.date} onChange={(e) => setF((s) => ({ ...s, date: e.target.value }))} />
            </div>
            <div className="axrc-f">
              <label htmlFor="rcEdDue">Due date</label>
              <input type="date" id="rcEdDue" className={f.due && f.date && f.due < f.date ? "err" : ""} value={f.due} onChange={(e) => setF((s) => ({ ...s, due: e.target.value }))} />
            </div>
          </div>
          <SectHead>Lines on this invoice only</SectHead>
          <div className="axrc-itemwrap">
            <table className="axrc-gtable compact">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num" style={{ width: 60 }}>
                    Qty
                  </th>
                  <th className="num" style={{ width: 84 }}>
                    Rate
                  </th>
                  <th className="num" style={{ width: 62 }}>
                    Disc %
                  </th>
                  <th className="num" style={{ width: 62 }}>
                    VAT %
                  </th>
                  <th className="num" style={{ width: 86 }}>
                    Net
                  </th>
                </tr>
              </thead>
              <tbody>
                {f.items.map((l, i) => (
                  <tr key={l.key}>
                    <td>{l.productName}</td>
                    <td>
                      <input className="num" type="number" min="0" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} />
                    </td>
                    <td>
                      <input className="num" type="number" min="0" step="0.01" value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} />
                    </td>
                    <td>
                      <input className="num" type="number" min="0" max="100" value={l.disc} onChange={(e) => setLine(i, { disc: e.target.value })} />
                    </td>
                    <td>
                      <input className="num" type="number" min="0" max="100" disabled={!l.taxable} value={l.vatPct} onChange={(e) => setLine(i, { vatPct: e.target.value })} />
                    </td>
                    <td className="calc num strong">{money2(lineOf(l).net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="axrc-callout info">
            Changes apply to this invoice only. To change every future cycle, edit the profile.
          </div>
        </>
      )}
    </Drawer>
  );
};
